import { useId, type ChangeEvent, type ReactNode } from 'react';

/**
 * Checkbox — input[type=checkbox] + label の単体 (Sprint AS、docs/16 §10-2 P5 設計)。
 *
 * 設計方針:
 *   - input は左、label テキストは右に配置 (一般的な checkbox レイアウト)
 *   - label 全体を `<label>` でラップし click target を広げる (a11y / Fitts の法則)
 *   - hint / error 規約は TextField と統一 (id 紐付け / aria-describedby / aria-invalid)
 *   - onChange は `(checked: boolean)` を渡す (TextField 同様、value のみ規約)
 */

export interface CheckboxProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  id?: string;
  className?: string;
}

const BOX_BASE =
  'h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-2 focus:ring-offset-1 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed';
const BOX_ERROR = 'border-danger-600 focus:ring-danger-600';

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  error,
  hint,
  id,
  className = '',
}: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={className}>
      <label htmlFor={inputId} className="inline-flex items-start gap-2 cursor-pointer">
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`${BOX_BASE} ${invalid ? BOX_ERROR : ''} mt-0.5`}
        />
        <span
          className={`text-sm ${disabled ? 'text-slate-400' : 'text-slate-700'} ${disabled ? 'cursor-not-allowed' : ''}`}
        >
          {label}
        </span>
      </label>
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-danger-600">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
